import { supabase } from '@/lib/supabaseClient';
import {
  ExpenseRecord,
  TransactionCategoryRecord,
  RateRuleRecord,
} from '@/types/database';
import { documentService } from '@/services/documentService';

export interface CreateExpensePayload {
  category_id: string;
  cycle_id?: string | null;
  farm_id?: string | null;
  supplier_id?: string | null;
  expense_date: string;
  amount: number;
  account_code?: string | null;
  description: string;
  reference_no?: string | null;
  notes?: string | null;
}

export const expenseService = {
  /**
   * Fetches active Chart of Accounts categories from transaction_categories.
   * Defaults to category_type = 'expense'.
   */
  async getTransactionCategories(categoryType = 'expense'): Promise<TransactionCategoryRecord[]> {
    let query = supabase
      .from('transaction_categories')
      .select('id, code, name, category_type, target_table, description, is_active')
      .eq('is_active', true)
      .order('code', { ascending: true });

    if (categoryType && categoryType !== 'all') {
      query = query.eq('category_type', categoryType);
    }

    const { data, error } = await query;
    if (error) {
      console.warn('[expenseService.getTransactionCategories] Warn:', error.message);
      return [];
    }

    return (data || []) as TransactionCategoryRecord[];
  },

  /**
   * Fetches active rate rules for suggestion helpers.
   */
  async getSuggestedRateRules(ruleCategory?: string): Promise<RateRuleRecord[]> {
    let query = supabase
      .from('rate_rules')
      .select('id, rule_name, rule_code, rule_category, rate_value, rate_unit, description, status, is_active')
      .eq('is_active', true)
      .eq('status', 'active')
      .order('rule_name', { ascending: true });

    if (ruleCategory) {
      query = query.eq('rule_category', ruleCategory);
    }

    const { data, error } = await query;
    if (error) {
      console.warn('[expenseService.getSuggestedRateRules] Warn:', error.message);
      return [];
    }

    return (data || []) as RateRuleRecord[];
  },

  /**
   * Fetches expenses with optional filters and attached documents.
   */
  async getExpenses(filters: {
    cycleId?: string;
    farmId?: string;
    categoryId?: string;
  } = {}): Promise<ExpenseRecord[]> {
    let query = supabase
      .from('expenses')
      .select(`
        *,
        transaction_categories (id, code, name, category_type),
        farms (id, farm_name),
        suppliers (id, supplier_code, supplier_name),
        production_cycles (id, cycle_code, status, operation_type, growers (full_name))
      `)
      .eq('is_void', false)
      .order('expense_date', { ascending: false });

    if (filters.cycleId) {
      query = query.eq('cycle_id', filters.cycleId);
    }
    if (filters.farmId) {
      query = query.eq('farm_id', filters.farmId);
    }
    if (filters.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[expenseService.getExpenses] Error:', error.message);
      throw error;
    }

    const expenses = (data || []) as unknown as ExpenseRecord[];

    for (const exp of expenses) {
      try {
        const docs = await documentService.getEntityDocuments('expenses', exp.id);
        exp.documents = docs;
      } catch {
        exp.documents = [];
      }
    }

    return expenses;
  },

  /**
   * Records a new operating expense linked to Chart of Accounts transaction_categories.
   */
  async createExpense(
    payload: CreateExpensePayload,
    attachedFile?: File | Blob | null
  ): Promise<ExpenseRecord> {
    if (!payload.category_id) throw new Error('Transaction category is required.');
    if (!payload.expense_date) throw new Error('Expense date is required.');
    if (payload.amount === undefined || payload.amount === null) {
      throw new Error('Expense amount is required.');
    }
    if (!payload.description || !payload.description.trim()) {
      throw new Error('Expense description is required.');
    }

    const expenseNumber = `EXP-${Date.now().toString().slice(-6)}`;

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const insertPayload: any = {
      expense_number: expenseNumber,
      category_id: payload.category_id,
      cycle_id: payload.cycle_id || null,
      farm_id: payload.farm_id || null,
      supplier_id: payload.supplier_id || null,
      expense_date: payload.expense_date,
      amount: Number(payload.amount),
      account_code: payload.account_code || null,
      description: payload.description.trim(),
      reference_no: payload.reference_no?.trim() || null,
      notes: payload.notes?.trim() || null,
      created_by: userId,
      updated_by: userId,
      is_void: false,
    };

    const { data: expenseRecord, error: insertError } = await supabase
      .from('expenses')
      .insert(insertPayload)
      .select(`
        *,
        transaction_categories (id, code, name),
        farms (id, farm_name),
        suppliers (id, supplier_name),
        production_cycles (id, cycle_code)
      `)
      .single();

    if (insertError) {
      console.error('[expenseService.createExpense] Insert error:', insertError);
      throw new Error(`Failed to record expense: ${insertError.message}`);
    }

    if (attachedFile && expenseRecord?.id) {
      try {
        const fileName = (attachedFile as File).name || `expense_receipt_${expenseNumber}.jpg`;
        await documentService.uploadDocument({
          file: attachedFile,
          fileName,
          entityType: 'expenses',
          entityId: expenseRecord.id,
          documentType: 'invoice',
          notes: `Expense receipt for ${expenseNumber}`,
        });
      } catch (docErr: any) {
        console.error('[expenseService.createExpense] Document attachment error:', docErr);
      }
    }

    return expenseRecord as unknown as ExpenseRecord;
  },

  /**
   * Soft-voids an expense with a mandatory audit reason.
   */
  async softVoidExpense(expenseId: string, voidReason: string): Promise<void> {
    if (!voidReason || !voidReason.trim()) {
      throw new Error('Mandatory void reason is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { error } = await supabase
      .from('expenses')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        voided_by: userId,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', expenseId);

    if (error) {
      throw new Error(`Failed to void expense: ${error.message}`);
    }
  },
};
